import { useQuery } from '@tanstack/react-query'
import { isEmpty } from 'lodash'

import { SelectAutocompleteProps } from '@forto-dev/phoenix-ui-core'
import { PortResponse, PortResponseStandardPagedResponse } from '@forto-dev/transport-network-client'

import Client from '@/api/client'
import { getConfig } from '@/app/config'

const basePath = '/api/transport-network/api/ports'
const client = new Client(getConfig().API_ENDPOINT ?? '')

export const getPortById = (portId: string) => {
  return client.get<PortResponse>(`${basePath}/${portId}`)
}

export const usePortByIdQuery = (portId = '') => {
  return useQuery({
    queryKey: ['ports', portId],
    queryFn: async () => getPortById(portId),
    refetchOnMount: false,
    enabled: !isEmpty(portId),
  })
}

export const usePortsByIdQuery = (portIds: string[]) =>
  useQuery({
    queryKey: ['ports', portIds],
    queryFn: () => getPortsByIDs(portIds),
    initialData: [],
  })

export const getPortsByIDs = async (ids?: string[]): Promise<PortResponse[]> => {
  if (!ids?.length) {
    return []
  }

  const { data } = await client.post<PortResponseStandardPagedResponse>(`${basePath}/search`, {
    ids: !isEmpty(ids) ? ids : undefined,
  })

  if (!data) {
    return []
  }

  return data
}

export const formatPort = (port: PortResponse): string => {
  return `${port.name} (${port.id})`
}

const getPortsAutocompleteItemsByQuery = async (
  query: string,
  modes?: string[],
  countryCodes?: string[]
): Promise<SelectAutocompleteProps['items']> => {
  const { data } = await client.post<PortResponseStandardPagedResponse>(`${basePath}/search?page=1&pageSize=20`, {
    query,
    modes: modes && modes.length ? modes : undefined,
    countryCodes: !isEmpty(countryCodes) ? countryCodes : undefined,
    attributes: ['TradeLaneEnabled'],
  })

  if (!data) {
    return []
  }

  return data.map((port) => ({
    label: formatPort(port),
    value: port.id,
  }))
}

type UsePortsQueryProps = {
  query: string
  modes?: string[]
  countryCodes?: string[]
}
export const usePortsQuery = ({ query, modes, countryCodes = [] }: UsePortsQueryProps) => {
  return useQuery({
    queryKey: ['ports', query, modes, countryCodes],
    queryFn: () => getPortsAutocompleteItemsByQuery(query, modes, countryCodes),
    initialData: [],
    enabled: !isEmpty(query),
  })
}
